import React, { useState } from 'react';
import { Theme, makeStyles } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import ImageUploadDialog from './ImageUploadDialog';
import { FileUploadResult, ImageProvider, Orientation, Shape } from '../types/types';
import Placeholder, { PlaceholderType } from './Placeholder';
import pencilIcon from './editpencil.svg';
import { useEntContext } from '../contexts';

interface CompanyLogoStyleProps {
  editable: boolean,
  addable: boolean
}

const useStyles = makeStyles<Theme, CompanyLogoStyleProps>((theme: Theme) => ({
  container: {
    width: 'auto',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    margin: '0',
    padding: '0',
    maxHeight: '100px'
  },
  imageCropper: {
    width: 'auto',
    height: 'auto',
    position: 'relative',
    overflow: (props) => (props.addable ? 'visible' : 'hidden'),

    '&:hover': {
      cursor: (props) => (props.editable ? 'pointer' : 'arrow')
    },

    '&:hover img': {
      opacity: (props) => (props.editable ? 0.5 : 1.0)
    },

    '&:hover #editWrapper': {
      opacity: (props) => (props.editable ? 1.0 : 0.0),
      border: `3px solid ${theme.palette.secondary.main}`,
      clickEvents: (props) => (props.editable ? 'auto' : 'none')
    },

    '&:hover #editWrapper img': {
      opacity: (props) => (props.editable ? 1.0 : 0.0)
    }
  },
  logoImage: {
    display: 'inline',
    margin: '0 auto',
    pointerEvents: 'auto',
    maxWidth: '300px',
    maxHeight: '100px'
  },
  pendingLogoImage: {
    display: 'inline',
    margin: '0 auto',
    height: '100%',
    width: 'auto',
    opacity: '0.5'
  },
  editWrapper: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    display: (props) => (props.editable ? 'flex' : 'none'),
    top: 0,
    left: 0,
    pointerEvents: (props) => (props.editable ? 'auto' : 'none'),
    opacity: 0
  },
  editIcon: {
    width: '50%',
    height: '50%'
  }
}));

interface CompanyLogoImageProps {
  logoImageProvider: ImageProvider
}

const CompanyLogoImage: React.FC<CompanyLogoImageProps> = ({
  logoImageProvider
}) => {
  const handleEditClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (setIsSelectingImage) {
      setIsSelectingImage(true);
    }
  };

  const { imgUrl, tempImgUrl, updateImage, editable, isSelectingImage, setIsSelectingImage } = logoImageProvider;
  const imgSrc = tempImgUrl || imgUrl;

  const addable = editable && !imgSrc;

  const styles = useStyles({ editable, addable });

  const handleUploadImageResult = async (result: Promise<FileUploadResult>) => {
    if (updateImage) {
      if (setIsSelectingImage) {
        setIsSelectingImage(false);
      }
      const uploadResult = await result;
      console.log('image data ready!', uploadResult);
      await updateImage(uploadResult);
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.imageCropper}>
        {
          imgSrc
            ? (
              <>
                <img
                  src={imgSrc}
                  className={tempImgUrl ? styles.pendingLogoImage : styles.logoImage}
                  alt='user_photo'
                />
                <Box className={styles.editWrapper} id='editWrapper' onClick={handleEditClick}>
                  <div className={styles.editIcon}>
                    <img src={pencilIcon} alt='' style={{ objectFit: 'contain', width: '100%', height: '100%' }} />
                  </div>
                </Box>
              </>
            )
            : (
              <Placeholder
                type={PlaceholderType.Image}
                text='COMPANY LOGO'
                shape={Shape.Rect}
                orientation={Orientation.Horizontal}
                iconOffsetPercent={10}
                onClick={addable ? handleEditClick : undefined}
                pad='25px 30px 25px 25px'
              />
            )
            }
      </Box>
      {
        editable && setIsSelectingImage
          ? (
            <ImageUploadDialog
              isOpen={isSelectingImage || false}
              handleClose={() => { setIsSelectingImage(false); }}
              onFinished={handleUploadImageResult}
              skipCrop
            />
          )
          : null
      }

    </Box>

  );
};

export default CompanyLogoImage;
